<script lang="ts">
    import type { Price, SaleTags } from '@shared/types/types';
    import BenefitTag from '@tile/components/benefit-tag/BenefitTag.svelte';
    import DealHighlight from '@tile/components/deal-highlight/DealHighlight.svelte';
    import Installments from '@tile/components/installments/Installments.svelte';
    import PriceRow from '@tile/components/price-row/PriceRow.svelte';
    import PriceContainer from '@tile/components/price/Price.svelte';
    import RetailPrice from '@tile/components/retail-price/RetailPrice.svelte';
    import SaleTag from '@tile/components/sale-tag/SaleTag.svelte';
    import SaleTagsComponent from '@tile/components/sale-tags/SaleTags.svelte';
    import SuggestedRetailPrice from '@tile/components/suggested-retail-price/SuggestedRetailPrice.svelte';
    import UnitPrice from '@tile/components/unit-price/UnitPrice.svelte';
    import { injectWiring } from '@tilelist/application/wiring/TileListWiringContext.svelte.ts';
    import type { ExperimentGroups } from '@tilelist/domain/experiments/Experiments.ts';
    import SuggestedRetailPriceExperiment from '@tilelist/layouts/experiments/e3823/suggested-retail-price/SuggestedRetailPrice.svelte';

    interface Props {
        price: Price;
        saleTags?: SaleTags;
        dealHighlight?: string;
        variationId: string;
        rule?: string;
        showInstallments?: boolean;
        bestVariationBenefit?: string;
    }

    let { price, saleTags, dealHighlight, variationId, rule, showInstallments, bestVariationBenefit }: Props = $props();

    const onex = injectWiring().onex;

    const uvpMapping: Record<ExperimentGroups['UVP_DISPLAY'], string> = {
        E3823A: 'statusQuo',
        E3823B: 'defensive',
        E3823C: 'strike',
        E3823D: 'red',
        E3823E: 'strikeRed',
    };

    const isUvpExperimentEligible = $derived(price.suggestedRetailPrice != undefined);
    const uvpExp = $derived(onex.matchGroup('UVP_DISPLAY', uvpMapping));
    const effectiveSaleTags = $derived(
        uvpExp === 'statusQuo' || !isUvpExperimentEligible
            ? saleTags
            : saleTags
              ? { ...saleTags, discount: undefined }
              : saleTags,
    );
    const saleTagsEmpty = $derived(!effectiveSaleTags?.discount && !bestVariationBenefit);
</script>

{#snippet priceContent()}
    <DealHighlight {dealHighlight} />
    <UnitPrice unitPrice={price.normPrice} />
    {#if showInstallments}
        <Installments installmentPlan={price.installmentPlan} />
    {/if}
    <SaleTagsComponent isEmpty={saleTagsEmpty}>
        <SaleTag discount={effectiveSaleTags?.discount} />
        <BenefitTag {bestVariationBenefit} {rule} {variationId} />
    </SaleTagsComponent>
{/snippet}

<PriceContainer>
    {#if uvpExp === 'statusQuo' || !isUvpExperimentEligible}
        <PriceRow>
            <RetailPrice {price} discounted={price.sale} />
            <SuggestedRetailPrice {price} />
        </PriceRow>
    {:else if uvpExp === 'defensive'}
        <PriceRow>
            <RetailPrice {price} discounted={false} />
            <SuggestedRetailPriceExperiment {price} strikethrough={false} />
        </PriceRow>
    {:else if uvpExp === 'strike'}
        <PriceRow>
            <RetailPrice {price} discounted={false} />
            <SuggestedRetailPriceExperiment {price} strikethrough={true} />
        </PriceRow>
    {:else if uvpExp === 'red'}
        <PriceRow>
            <RetailPrice {price} discounted={true} />
            <SuggestedRetailPriceExperiment {price} strikethrough={false} />
        </PriceRow>
    {:else if uvpExp === 'strikeRed'}
        <PriceRow>
            <RetailPrice {price} discounted={true} />
            <SuggestedRetailPriceExperiment {price} strikethrough={true} />
        </PriceRow>
    {/if}
    {@render priceContent()}
</PriceContainer>
